import { useRef, useState } from "react";

import { useCan } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { ErrorNotice } from "../components/ui";
import { ImportPreview } from "./ImportPreview";
import { ImportResult } from "./ImportResult";
import {
  useCommitWorkbook,
  useDownloadTemplate,
  useValidateWorkbook,
} from "./useBulkProducts";

/**
 * Choose a workbook, see what it would do, then decide.
 *
 * CHOOSING A FILE NEVER IMPORTS IT. Selecting validates — which writes nothing
 * — and confirming is a separate press of a separate button. An upload that
 * applied itself would make the preview a report of something already done.
 *
 * THE FILE STAYS HERE. Confirmation sends the same workbook back and the server
 * re-derives the whole plan from it.
 *
 * THE WORKBOOK IS CATALOGUE DATA, AND ONLY THAT. Bulk creates and updates
 * products; it does not photograph them. Photography belongs to the product it
 * is of: it is chosen per variant, in the family's own vocabulary, on the
 * product's own Edit screen, where the operator can see what they are attaching
 * it to. A folder of files matched to rows by name is a guess made far away
 * from the product, and this screen deliberately does not make it. Everything
 * imported arrives as a Draft, and the result links to each new one.
 */
export function ImportPanel() {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const validate = useValidateWorkbook();
  const commit = useCommitWorkbook();
  const template = useDownloadTemplate();

  const preview = validate.data ?? null;
  const result = commit.data ?? null;

  // A preview with errors is a preview of something that will not be applied.
  // Checking a workbook is a read and applying one is a catalogue mutation
  // across many products at once, so they are separate authorities: somebody
  // who may only READ Bulk can see exactly what a file would do and cannot
  // make it happen.
  const canCommit = useCan(CAP.bulkCommit);
  const canConfirm =
    canCommit &&
    Boolean(file && preview) &&
    preview!.errors === 0 &&
    preview!.creates + preview!.updates > 0 &&
    !commit.isPending;

  function reset() {
    setFile(null);
    validate.reset();
    commit.reset();
    if (input.current) input.current.value = "";
  }

  function choose(chosen: File | null) {
    commit.reset();
    setFile(chosen);
    if (chosen) validate.mutate(chosen);
    else validate.reset();
  }

  // `mutate`, not `mutateAsync`: nothing happens after the commit, and a
  // refused one is already on the screen through `commit.error`. Awaiting it
  // from a click handler would only give the rejection somewhere to escape to.
  function confirm() {
    if (!file || !preview) return;
    commit.mutate({ file, planDigest: preview.plan_digest });
  }

  if (result) {
    return (
      <section className="panel bulk__panel" aria-labelledby="bulk-import">
        <h2 id="bulk-import" className="panel__title">
          Import result
        </h2>
        <div className="panel__body">
          <ImportResult result={result} onStartOver={reset} />
        </div>
      </section>
    );
  }

  return (
    <section className="panel bulk__panel" aria-labelledby="bulk-import">
      <h2 id="bulk-import" className="panel__title">
        Import products
      </h2>
      <div className="panel__body stack">
        <p className="muted">
          Edit a workbook and check it here before anything is applied. Start
          from an export of the products you want to change, or from the empty
          template.
        </p>

        <div className="row">
          <label className="btn" htmlFor="bulk-file">
            Choose workbook
          </label>
          <input
            id="bulk-file"
            ref={input}
            type="file"
            className="bulk__file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => choose(event.target.files?.[0] ?? null)}
          />
          <span className="muted" data-testid="chosen-file">
            {file ? file.name : "No workbook chosen"}
          </span>
          <button
            type="button"
            className="btn btn--subtle"
            disabled={template.isPending}
            onClick={() => template.mutate()}
          >
            {template.isPending ? "Preparing…" : "Download template"}
          </button>
        </div>

        {template.isError ? <ErrorNotice error={template.error} /> : null}

        {validate.isPending ? (
          <div className="empty" role="status">
            Checking {file?.name}…
          </div>
        ) : null}

        {validate.isError ? (
          <div className="bulk__failure">
            <ErrorNotice error={validate.error} />
            <button
              type="button"
              className="btn"
              onClick={() => file && validate.mutate(file)}
            >
              Check again
            </button>
          </div>
        ) : null}

        {preview && file ? (
          <>
            <ImportPreview preview={preview} filename={file.name} />

            {commit.isError ? <ErrorNotice error={commit.error} /> : null}

            <div className="row bulk__confirm">
              {canCommit ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!canConfirm}
                  onClick={confirm}
                >
                  {commit.isPending ? "Importing…" : "Import these products"}
                </button>
              ) : (
                <span className="muted" role="status" data-testid="bulk-commit-denied">
                  You don&apos;t have permission to import products.
                </span>
              )}
              <button type="button" className="btn" onClick={reset}>
                Cancel
              </button>
              {preview.errors > 0 ? (
                <span className="muted" role="status">
                  Correct the errors in the workbook and check it again.
                </span>
              ) : preview.creates + preview.updates === 0 ? (
                <span className="muted" role="status">
                  Nothing to import: this workbook matches what the shop already
                  has.
                </span>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
